// Predefined theme palettes for MockForge
// Each theme defines light and dark variants with CSS custom property values

export interface ThemePalette {
  id: string;
  name: string;
  description: string;
  preview: {
    primary: string;
    secondary: string;
    accent: string;
  };
  colors: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
}

// Core brand theme (current MockForge colors)
export const coreBrandTheme: ThemePalette = {
  id: 'core-brand',
  name: 'MockForge Core',
  description: 'Professional orange-themed design with clean contrast',
  preview: {
    primary: '#D35400',
    secondary: '#FAFBFC',
    accent: '#10B981',
  },
  colors: {
    light: {
      // Background colors
      '--background': '0 0% 100%',
      '--card': '0 0% 100%',
      '--card-foreground': '222 24% 14%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '222 24% 14%',
      '--primary': '24 86% 42%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '220 14% 95%',
      '--secondary-foreground': '222 24% 14%',
      '--muted': '220 14% 95.5%',
      '--muted-foreground': '220 9% 42%',
      '--accent': '220 14% 95.5%',
      '--accent-foreground': '222 24% 14%',
      '--destructive': '0 84% 50%',
      '--destructive-foreground': '0 0% 98%',
      '--border': '220 13% 90%',
      '--input': '220 13% 88%',
      '--ring': '24 86% 45%',
      '--brand': '24 86% 42%',
      '--brand-50': '24 100% 97%',
      '--brand-100': '24 95% 92%',
      '--brand-200': '24 90% 84%',
      '--brand-300': '24 85% 70%',
      '--brand-400': '24 85% 55%',
      '--brand-500': '24 86% 42%',
      '--brand-600': '24 88% 36%',
      '--brand-700': '24 92% 30%',
      '--brand-800': '24 95% 24%',
      '--brand-900': '24 98% 18%',
      '--success': '142 76% 36%',
      '--success-50': '142 100% 97%',
      '--success-100': '142 90% 92%',
      '--success-500': '142 76% 36%',
      '--success-600': '142 78% 32%',
      '--warning': '42 96% 50%',
      '--warning-50': '42 100% 96%',
      '--warning-100': '42 95% 90%',
      '--warning-500': '42 96% 50%',
      '--warning-600': '42 98% 45%',
      '--danger': '0 84% 50%',
      '--danger-50': '0 100% 97%',
      '--danger-100': '0 95% 92%',
      '--danger-500': '0 84% 50%',
      '--danger-600': '0 86% 45%',
      '--info': '217 91% 60%',
      '--info-50': '217 100% 97%',
      '--info-100': '217 95% 92%',
      '--info-500': '217 91% 60%',
      '--info-600': '217 93% 55%',
      '--bg-primary': '0 0% 100%',
      '--bg-secondary': '220 20% 98%',
      '--bg-tertiary': '220 14% 96%',
      '--bg-overlay': '0 0% 0% / 0.5',
      '--text-primary': '222 24% 14%',
      '--text-secondary': '220 9% 38%',
      '--text-tertiary': '220 8% 50%',
      '--text-inverse': '0 0% 100%',
      '--radius': '12px',
    },
    dark: {
      '--background': '222 14% 6%',
      '--card': '222 13% 8.5%',
      '--card-foreground': '220 14% 96%',
      '--popover': '222 13% 10%',
      '--popover-foreground': '220 14% 96%',
      '--primary': '24 86% 52%',
      '--primary-foreground': '220 15% 7%',
      '--secondary': '222 10% 15%',
      '--secondary-foreground': '220 14% 96%',
      '--muted': '222 10% 14%',
      '--muted-foreground': '220 9% 66%',
      '--accent': '222 10% 15%',
      '--accent-foreground': '220 14% 96%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '220 15% 7%',
      '--border': '222 9% 18%',
      '--input': '222 9% 20%',
      '--ring': '24 86% 52%',
      '--brand': '24 86% 52%',
      '--brand-50': '24 15% 12%',
      '--brand-100': '24 20% 18%',
      '--brand-200': '24 25% 24%',
      '--brand-300': '24 30% 30%',
      '--brand-400': '24 40% 40%',
      '--brand-500': '24 86% 52%',
      '--brand-600': '24 88% 48%',
      '--brand-700': '24 92% 42%',
      '--brand-800': '24 95% 36%',
      '--brand-900': '24 98% 24%',
      '--success': '142 76% 48%',
      '--success-50': '142 15% 12%',
      '--success-100': '142 20% 18%',
      '--success-500': '142 76% 48%',
      '--success-600': '142 78% 42%',
      '--warning': '42 96% 60%',
      '--warning-50': '42 15% 12%',
      '--warning-100': '42 20% 18%',
      '--warning-500': '42 96% 60%',
      '--warning-600': '42 98% 55%',
      '--danger': '0 84% 60%',
      '--danger-50': '0 15% 12%',
      '--danger-100': '0 20% 18%',
      '--danger-500': '0 84% 60%',
      '--danger-600': '0 86% 55%',
      '--info': '217 91% 65%',
      '--info-50': '217 15% 12%',
      '--info-100': '217 20% 18%',
      '--info-500': '217 91% 65%',
      '--info-600': '217 93% 60%',
      '--bg-primary': '222 13% 8.5%',
      '--bg-secondary': '222 14% 6%',
      '--bg-tertiary': '222 10% 12%',
      '--bg-overlay': '0 0% 0% / 0.7',
      '--text-primary': '220 14% 96%',
      '--text-secondary': '220 10% 78%',
      '--text-tertiary': '220 8% 62%',
      '--text-inverse': '220 15% 7%',
      '--radius': '12px',
    },
  },
};

// Professional blue theme
export const professionalBlueTheme: ThemePalette = {
  id: 'professional-blue',
  name: 'Professional Blue',
  description: 'Clean corporate blue for business environments',
  preview: {
    primary: '#2563EB',
    secondary: '#F8FAFC',
    accent: '#10B981',
  },
  colors: {
    light: {
      '--background': '0 0% 100%',
      '--card': '0 0% 100%',
      '--card-foreground': '222.2 84% 4.9%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '222.2 84% 4.9%',
      '--primary': '221.2 83.2% 53.3%',
      '--primary-foreground': '210 40% 98%',
      '--secondary': '210 40% 96%',
      '--secondary-foreground': '222.2 84% 4.9%',
      '--muted': '210 40% 96%',
      '--muted-foreground': '215.4 16.3% 46.9%',
      '--accent': '210 40% 96%',
      '--accent-foreground': '222.2 84% 4.9%',
      '--destructive': '0 84.2% 60.2%',
      '--destructive-foreground': '210 40% 98%',
      '--border': '214.3 31.8% 91.4%',
      '--input': '214.3 31.8% 91.4%',
      '--ring': '221.2 83.2% 53.3%',
      '--brand': '221.2 83.2% 53.3%',
      '--brand-50': '214.3 31.8% 91.4%',
      '--brand-100': '214.3 31.8% 91.4%',
      '--brand-200': '209.4 31.8% 84%',
      '--brand-300': '209.4 34% 70%',
      '--brand-400': '217.2 32% 54%',
      '--brand-500': '221.2 83.2% 53.3%',
      '--brand-600': '217.2 91.2% 60%',
      '--brand-700': '217.2 91.2% 42%',
      '--brand-800': '217.2 93.2% 30%',
      '--brand-900': '217.2 93.2% 18%',
      '--success': '142 76.2% 36.3%',
      '--success-50': '138 62.8% 94.6%',
      '--success-100': '134 60.3% 84%',
      '--success-500': '142 76.2% 36.3%',
      '--success-600': '142 71% 45%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '53.8 91.8% 94.6%',
      '--warning-100': '49.8 91.7% 84%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 84.2% 60.2%',
      '--danger-50': '0 85.7% 97.3%',
      '--danger-100': '0 74.7% 84%',
      '--danger-500': '0 84.2% 60.2%',
      '--danger-600': '0 72% 51%',
      '--info': '199.4 89% 48.3%',
      '--info-50': '199.4 89% 95%',
      '--info-100': '197.4 89% 84%',
      '--info-500': '199.4 89% 48.3%',
      '--info-600': '202.4 89% 48%',
      '--bg-primary': '0 0% 100%',
      '--bg-secondary': '210 40% 98%',
      '--bg-tertiary': '210 40% 96%',
      '--bg-overlay': '0 0% 0% / 0.5',
      '--text-primary': '222.2 84% 4.9%',
      '--text-secondary': '215.4 16.3% 46.9%',
      '--text-tertiary': '215 13.8% 34.1%',
      '--text-inverse': '0 0% 100%',
      '--radius': '12px',
    },
    dark: {
      '--background': '222.2 84% 4.9%',
      '--card': '222.2 84% 4.9%',
      '--card-foreground': '210 40% 98%',
      '--popover': '222.2 84% 4.9%',
      '--popover-foreground': '210 40% 98%',
      '--primary': '217.2 91.2% 59.8%',
      '--primary-foreground': '222.2 84% 4.9%',
      '--secondary': '217.2 32.6% 17.5%',
      '--secondary-foreground': '210 40% 98%',
      '--muted': '217.2 32.6% 17.5%',
      '--muted-foreground': '215 20.2% 65.1%',
      '--accent': '217.2 32.6% 17.5%',
      '--accent-foreground': '210 40% 98%',
      '--destructive': '0 62.8% 30.6%',
      '--destructive-foreground': '210 40% 98%',
      '--border': '217.2 32.6% 17.5%',
      '--input': '217.2 32.6% 17.5%',
      '--ring': '224.3 76.3% 94.1%',
      '--brand': '217.2 91.2% 59.8%',
      '--brand-50': '210 5% 8%',
      '--brand-100': '214.3 31.8% 10%',
      '--brand-200': '217.2 32.6% 17.5%',
      '--brand-300': '216.5 41% 25%',
      '--brand-400': '217.2 32% 35%',
      '--brand-500': '217.2 91.2% 59.8%',
      '--brand-600': '217.2 91.2% 46%',
      '--brand-700': '217.2 91.2% 35%',
      '--brand-800': '217.2 91.2% 25%',
      '--brand-900': '217.2 91.2% 15%',
      '--success': '142 76.2% 36.3%',
      '--success-50': '120 5% 8%',
      '--success-100': '125 60% 10%',
      '--success-500': '142 76.2% 36.3%',
      '--success-600': '142 71% 45%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '36 4% 8%',
      '--warning-100': '36 93% 10%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 62.8% 30.6%',
      '--danger-50': '0 5% 6%',
      '--danger-100': '0 45% 10%',
      '--danger-500': '0 62.8% 30.6%',
      '--danger-600': '0 72% 51%',
      '--info': '199.4 89% 48.3%',
      '--info-50': '180 4% 8%',
      '--info-100': '180 89% 10%',
      '--info-500': '199.4 89% 48.3%',
      '--info-600': '202.4 89% 48%',
      '--bg-primary': '222.2 84% 4.9%',
      '--bg-secondary': '217.2 32.6% 17.5%',
      '--bg-tertiary': '217.2 32.6% 17.5%',
      '--bg-overlay': '0 0% 0% / 0.7',
      '--text-primary': '210 40% 98%',
      '--text-secondary': '215 20.2% 65.1%',
      '--text-tertiary': '217.2 10.6% 64.9%',
      '--text-inverse': '222.2 84% 4.9%',
      '--radius': '12px',
    },
  },
};

// Forest green theme
export const forestGreenTheme: ThemePalette = {
  id: 'forest-green',
  name: 'Forest Green',
  description: 'Natural green palette inspired by forest landscapes',
  preview: {
    primary: '#16A34A',
    secondary: '#F0FDF4',
    accent: '#DC2626',
  },
  colors: {
    light: {
      '--background': '0 0% 100%',
      '--card': '0 0% 100%',
      '--card-foreground': '222.2 84% 4.9%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '222.2 84% 4.9%',
      '--primary': '142 76% 37%',
      '--primary-foreground': '210 40% 98%',
      '--secondary': '141 84% 96%',
      '--secondary-foreground': '222.2 84% 4.9%',
      '--muted': '141 84% 96%',
      '--muted-foreground': '215.4 16.3% 46.9%',
      '--accent': '141 84% 96%',
      '--accent-foreground': '222.2 84% 4.9%',
      '--destructive': '0 84.2% 60.2%',
      '--destructive-foreground': '210 40% 98%',
      '--border': '141 84% 93%',
      '--input': '141 84% 93%',
      '--ring': '142 76% 37%',
      '--brand': '142 76% 37%',
      '--brand-50': '137 88% 96%',
      '--brand-100': '136 82% 93%',
      '--brand-200': '141 64% 84%',
      '--brand-300': '142 69% 70%',
      '--brand-400': '142 78% 55%',
      '--brand-500': '142 76% 37%',
      '--brand-600': '142 76% 26%',
      '--brand-700': '142 76% 20%',
      '--brand-800': '142 76% 15%',
      '--brand-900': '142 76% 10%',
      '--success': '142 76% 37%',
      '--success-50': '141 84% 96%',
      '--success-100': '141 84% 93%',
      '--success-500': '142 76% 37%',
      '--success-600': '142 76% 26%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '53.8 91.8% 94.6%',
      '--warning-100': '49.8 91.7% 84%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 84.2% 60.2%',
      '--danger-50': '0 85.7% 97.3%',
      '--danger-100': '0 74.7% 84%',
      '--danger-500': '0 84.2% 60.2%',
      '--danger-600': '0 72% 51%',
      '--info': '221.2 83.2% 53.3%',
      '--info-50': '214.3 31.8% 91.4%',
      '--info-100': '214.3 31.8% 91.4%',
      '--info-500': '221.2 83.2% 53.3%',
      '--info-600': '217.2 91.2% 60%',
      '--bg-primary': '0 0% 100%',
      '--bg-secondary': '141 84% 96%',
      '--bg-tertiary': '141 84% 93%',
      '--bg-overlay': '0 0% 0% / 0.5',
      '--text-primary': '222.2 84% 4.9%',
      '--text-secondary': '215.4 16.3% 46.9%',
      '--text-tertiary': '215 13.8% 34.1%',
      '--text-inverse': '0 0% 100%',
      '--radius': '12px',
    },
    dark: {
      '--background': '120 10% 3.9%',
      '--card': '120 10% 3.9%',
      '--card-foreground': '120 9% 98%',
      '--popover': '120 10% 3.9%',
      '--popover-foreground': '120 9% 98%',
      '--primary': '142 76% 37%',
      '--primary-foreground': '120 10% 3.9%',
      '--secondary': '120 5% 10%',
      '--secondary-foreground': '120 9% 98%',
      '--muted': '120 5% 10%',
      '--muted-foreground': '120 5% 65%',
      '--accent': '120 5% 10%',
      '--accent-foreground': '120 9% 98%',
      '--destructive': '0 62.8% 30.6%',
      '--destructive-foreground': '120 9% 98%',
      '--border': '120 3.7% 15.9%',
      '--input': '120 3.7% 15.9%',
      '--ring': '142 76% 37%',
      '--brand': '142 76% 37%',
      '--brand-50': '120 3% 8%',
      '--brand-100': '120 5% 10%',
      '--brand-200': '120 5% 15%',
      '--brand-300': '120 5% 20%',
      '--brand-400': '120 5% 25%',
      '--brand-500': '142 76% 37%',
      '--brand-600': '142 76% 26%',
      '--brand-700': '142 76% 20%',
      '--brand-800': '142 76% 15%',
      '--brand-900': '142 76% 10%',
      '--success': '142 76% 37%',
      '--success-50': '120 5% 10%',
      '--success-100': '120 5% 15%',
      '--success-500': '142 76% 37%',
      '--success-600': '142 76% 26%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '36 4% 8%',
      '--warning-100': '36 93% 10%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 62.8% 30.6%',
      '--danger-50': '0 5% 6%',
      '--danger-100': '0 45% 10%',
      '--danger-500': '0 62.8% 30.6%',
      '--danger-600': '0 72% 51%',
      '--info': '221.2 83.2% 53.3%',
      '--info-50': '214.3 31% 8%',
      '--info-100': '214.3 31% 12%',
      '--info-500': '221.2 83.2% 53.3%',
      '--info-600': '217.2 91.2% 60%',
      '--bg-primary': '120 10% 3.9%',
      '--bg-secondary': '120 5% 10%',
      '--bg-tertiary': '120 3.7% 15.9%',
      '--bg-overlay': '0 0% 0% / 0.7',
      '--text-primary': '120 9% 98%',
      '--text-secondary': '120 5% 65%',
      '--text-tertiary': '120 3% 50%',
      '--text-inverse': '120 10% 3.9%',
      '--radius': '12px',
    },
  },
};

// Purple twilight theme
export const twilightPurpleTheme: ThemePalette = {
  id: 'twilight-purple',
  name: 'Twilight Purple',
  description: 'Elegant purple gradient inspired by sunset skies',
  preview: {
    primary: '#8B5CF6',
    secondary: '#FAF5FF',
    accent: '#F59E0B',
  },
  colors: {
    light: {
      '--background': '0 0% 100%',
      '--card': '0 0% 100%',
      '--card-foreground': '222.2 84% 4.9%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '222.2 84% 4.9%',
      '--primary': '255 92% 61%',
      '--primary-foreground': '210 40% 98%',
      '--secondary': '255 92% 96%',
      '--secondary-foreground': '222.2 84% 4.9%',
      '--muted': '255 92% 96%',
      '--muted-foreground': '215.4 16.3% 46.9%',
      '--accent': '255 92% 96%',
      '--accent-foreground': '222.2 84% 4.9%',
      '--destructive': '0 84.2% 60.2%',
      '--destructive-foreground': '210 40% 98%',
      '--border': '255 92% 93%',
      '--input': '255 92% 93%',
      '--ring': '255 92% 61%',
      '--brand': '255 92% 61%',
      '--brand-50': '261 91% 96%',
      '--brand-100': '261 91% 93%',
      '--brand-200': '261 91% 84%',
      '--brand-300': '262 83% 75%',
      '--brand-400': '261 91% 65%',
      '--brand-500': '255 92% 61%',
      '--brand-600': '262 83% 50%',
      '--brand-700': '262 83% 35%',
      '--brand-800': '262 83% 25%',
      '--brand-900': '262 83% 15%',
      '--success': '142 76.2% 36.3%',
      '--success-50': '138 62.8% 94.6%',
      '--success-100': '134 60.3% 84%',
      '--success-500': '142 76.2% 36.3%',
      '--success-600': '142 71% 45%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '53.8 91.8% 94.6%',
      '--warning-100': '49.8 91.7% 84%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 84.2% 60.2%',
      '--danger-50': '0 85.7% 97.3%',
      '--danger-100': '0 74.7% 84%',
      '--danger-500': '0 84.2% 60.2%',
      '--danger-600': '0 72% 51%',
      '--info': '221.2 83.2% 53.3%',
      '--info-50': '214.3 31.8% 91.4%',
      '--info-100': '214.3 31.8% 84%',
      '--info-500': '221.2 83.2% 53.3%',
      '--info-600': '217.2 91.2% 60%',
      '--bg-primary': '0 0% 100%',
      '--bg-secondary': '255 92% 96%',
      '--bg-tertiary': '261 91% 93%',
      '--bg-overlay': '0 0% 0% / 0.5',
      '--text-primary': '222.2 84% 4.9%',
      '--text-secondary': '215.4 16.3% 46.9%',
      '--text-tertiary': '215 13.8% 34.1%',
      '--text-inverse': '0 0% 100%',
      '--radius': '12px',
    },
    dark: {
      '--background': '255 10% 3.9%',
      '--card': '255 10% 3.9%',
      '--card-foreground': '255 10% 98%',
      '--popover': '255 10% 3.9%',
      '--popover-foreground': '255 10% 98%',
      '--primary': '255 92% 61%',
      '--primary-foreground': '255 10% 3.9%',
      '--secondary': '255 20% 10%',
      '--secondary-foreground': '255 10% 98%',
      '--muted': '255 20% 10%',
      '--muted-foreground': '255 10% 65%',
      '--accent': '255 20% 10%',
      '--accent-foreground': '255 10% 98%',
      '--destructive': '0 62.8% 30.6%',
      '--destructive-foreground': '255 10% 98%',
      '--border': '255 10% 15.9%',
      '--input': '255 10% 15.9%',
      '--ring': '255 92% 61%',
      '--brand': '255 92% 61%',
      '--brand-50': '255 5% 8%',
      '--brand-100': '255 15% 10%',
      '--brand-200': '255 20% 15%',
      '--brand-300': '255 20% 20%',
      '--brand-400': '255 25% 25%',
      '--brand-500': '255 92% 61%',
      '--brand-600': '262 83% 50%',
      '--brand-700': '262 83% 35%',
      '--brand-800': '262 83% 25%',
      '--brand-900': '262 83% 15%',
      '--success': '142 76.2% 36.3%',
      '--success-50': '125 15% 8%',
      '--success-100': '125 20% 12%',
      '--success-500': '142 76.2% 36.3%',
      '--success-600': '142 71% 45%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '36 4% 8%',
      '--warning-100': '36 93% 10%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 62.8% 30.6%',
      '--danger-50': '0 5% 6%',
      '--danger-100': '0 45% 10%',
      '--danger-500': '0 62.8% 30.6%',
      '--danger-600': '0 72% 51%',
      '--info': '221.2 83.2% 53.3%',
      '--info-50': '214.3 31% 8%',
      '--info-100': '214.3 31% 12%',
      '--info-500': '221.2 83.2% 53.3%',
      '--info-600': '217.2 91.2% 60%',
      '--bg-primary': '255 10% 3.9%',
      '--bg-secondary': '255 20% 10%',
      '--bg-tertiary': '255 10% 15.9%',
      '--bg-overlay': '0 0% 0% / 0.7',
      '--text-primary': '255 10% 98%',
      '--text-secondary': '255 10% 65%',
      '--text-tertiary': '255 10% 50%',
      '--text-inverse': '255 10% 3.9%',
      '--radius': '12px',
    },
  },
};

// Ocean deep theme
export const oceanDeepTheme: ThemePalette = {
  id: 'ocean-deep',
  name: 'Ocean Deep',
  description: 'Deep blue waves with teal accents for a nautical feel',
  preview: {
    primary: '#0EA5E9',
    secondary: '#F0F9FF',
    accent: '#EC4899',
  },
  colors: {
    light: {
      '--background': '0 0% 100%',
      '--card': '0 0% 100%',
      '--card-foreground': '222.2 84% 4.9%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '222.2 84% 4.9%',
      '--primary': '199 89% 48%',
      '--primary-foreground': '210 40% 98%',
      '--secondary': '204 100% 97%',
      '--secondary-foreground': '222.2 84% 4.9%',
      '--muted': '204 100% 97%',
      '--muted-foreground': '215.4 16.3% 46.9%',
      '--accent': '204 100% 97%',
      '--accent-foreground': '222.2 84% 4.9%',
      '--destructive': '0 84.2% 60.2%',
      '--destructive-foreground': '210 40% 98%',
      '--border': '204 94% 94%',
      '--input': '204 94% 94%',
      '--ring': '199 89% 48%',
      '--brand': '199 89% 48%',
      '--brand-50': '204 100% 97%',
      '--brand-100': '204 100% 97%',
      '--brand-200': '204 94% 90%',
      '--brand-300': '204 94% 82%',
      '--brand-400': '202 82% 65%',
      '--brand-500': '199 89% 48%',
      '--brand-600': '199 89% 38%',
      '--brand-700': '199 89% 28%',
      '--brand-800': '199 89% 18%',
      '--brand-900': '199 89% 12%',
      '--success': '142 76.2% 36.3%',
      '--success-50': '138 62.8% 94.6%',
      '--success-100': '134 60.3% 84%',
      '--success-500': '142 76.2% 36.3%',
      '--success-600': '142 71% 45%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '53.8 91.8% 94.6%',
      '--warning-100': '49.8 91.7% 84%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 84.2% 60.2%',
      '--danger-50': '0 85.7% 97.3%',
      '--danger-100': '0 74.7% 84%',
      '--danger-500': '0 84.2% 60.2%',
      '--danger-600': '0 72% 51%',
      '--info': '199 89% 48%',
      '--info-50': '204 100% 97%',
      '--info-100': '204 94% 94%',
      '--info-500': '199 89% 48%',
      '--info-600': '199 89% 38%',
      '--bg-primary': '0 0% 100%',
      '--bg-secondary': '204 100% 97%',
      '--bg-tertiary': '204 94% 94%',
      '--bg-overlay': '0 0% 0% / 0.5',
      '--text-primary': '222.2 84% 4.9%',
      '--text-secondary': '215.4 16.3% 46.9%',
      '--text-tertiary': '215 13.8% 34.1%',
      '--text-inverse': '0 0% 100%',
      '--radius': '12px',
    },
    dark: {
      '--background': '222 84% 4.9%',
      '--card': '222 84% 4.9%',
      '--card-foreground': '210 40% 98%',
      '--popover': '222 84% 4.9%',
      '--popover-foreground': '210 40% 98%',
      '--primary': '199 89% 48%',
      '--primary-foreground': '222 84% 4.9%',
      '--secondary': '217.2 32.6% 17.5%',
      '--secondary-foreground': '210 40% 98%',
      '--muted': '217.2 32.6% 17.5%',
      '--muted-foreground': '215 20.2% 65.1%',
      '--accent': '217.2 32.6% 17.5%',
      '--accent-foreground': '210 40% 98%',
      '--destructive': '0 62.8% 30.6%',
      '--destructive-foreground': '210 40% 98%',
      '--border': '217.2 32.6% 17.5%',
      '--input': '217.2 32.6% 17.5%',
      '--ring': '199 89% 48%',
      '--brand': '199 89% 48%',
      '--brand-50': '210 100% 8%',
      '--brand-100': '210 50% 12%',
      '--brand-200': '210 40% 16%',
      '--brand-300': '210 30% 20%',
      '--brand-400': '210 20% 24%',
      '--brand-500': '199 89% 48%',
      '--brand-600': '199 89% 38%',
      '--brand-700': '199 89% 28%',
      '--brand-800': '199 89% 18%',
      '--brand-900': '199 89% 12%',
      '--success': '142 76.2% 36.3%',
      '--success-50': '125 100% 8%',
      '--success-100': '125 80% 12%',
      '--success-500': '142 76.2% 36.3%',
      '--success-600': '142 71% 45%',
      '--warning': '45.4 93.4% 47.5%',
      '--warning-50': '36 80% 8%',
      '--warning-100': '36 93% 10%',
      '--warning-500': '45.4 93.4% 47.5%',
      '--warning-600': '35.5 91.7% 52%',
      '--danger': '0 62.8% 30.6%',
      '--danger-50': '0 80% 6%',
      '--danger-100': '0 45% 10%',
      '--danger-500': '0 62.8% 30.6%',
      '--danger-600': '0 72% 51%',
      '--info': '199 89% 48%',
      '--info-50': '210 100% 8%',
      '--info-100': '210 90% 12%',
      '--info-500': '199 89% 48%',
      '--info-600': '199 89% 38%',
      '--bg-primary': '222 84% 4.9%',
      '--bg-secondary': '217.2 32.6% 17.5%',
      '--bg-tertiary': '217.2 32.6% 17.5%',
      '--bg-overlay': '0 0% 0% / 0.7',
      '--text-primary': '210 40% 98%',
      '--text-secondary': '215 20.2% 65.1%',
      '--text-tertiary': '217.2 10.6% 64.9%',
      '--text-inverse': '222 84% 4.9%',
      '--radius': '12px',
    },
  },
};

// Export all predefined themes
export const predefinedThemes: ThemePalette[] = [
  coreBrandTheme,
  professionalBlueTheme,
  forestGreenTheme,
  twilightPurpleTheme,
  oceanDeepTheme,
];

// Utilities
export function getThemeById(id: string): ThemePalette | undefined {
  return predefinedThemes.find(theme => theme.id === id);
}

export function getDefaultTheme(): ThemePalette {
  return coreBrandTheme;
}

export function createCustomTheme(
  name: string,
  description: string,
  lightColors: Record<string, string>,
  darkColors: Record<string, string>
): ThemePalette {
  return {
    id: `custom-${Date.now()}`,
    name,
    description,
    preview: {
      primary: lightColors['--brand'] || '#000000',
      secondary: lightColors['--bg-secondary'] || '#FFFFFF',
      accent: lightColors['--success'] || '#10B981',
    },
    colors: {
      light: lightColors,
      dark: darkColors,
    },
  };
}
